import { Sparkles } from "lucide-react";

function ImpactStat({ display, label, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-3xl lg:text-4xl font-heading font-black text-teal-700 arabic-numerals">{display}</div>
      <div className="text-xs text-slate-500 mt-1">{label}</div>
    </div>
  );
}

export default function ImpactSection({ landing }) {
  const stats = landing?.stats;
  const top = landing?.top_student;

  const studentsDisplay = stats?.students?.display ?? "+1000";
  const certsDisplay = stats?.certificates?.display ?? "+120";
  const assocsDisplay = stats?.associations?.display ?? "+50";
  const opportunitiesDisplay = stats?.opportunities?.display ?? "0";
  const placementsDisplay = stats?.placements?.display ?? "0";
  const tasksDisplay = stats?.tasks?.display ?? "0";
  const completedHoursDisplay = stats?.completed_hours?.display ?? "0";
  const sessionsDisplay = stats?.sessions?.display ?? "0";

  // Fallback template values so the design stays identical when no top student is picked
  const card = top || {
    name: "طالب متدرب",
    program_label: "فرصة غير محددة",
    hours: 48,
    tasks_done: 9,
    tasks_total: 12,
    eval_pct: 94,
    attendance_pct: 96,
    progress_pct: 78,
    association_label: "جمعية غير محددة",
    since_months: 3,
  };

  return (
    <section className="py-20 lg:py-28 bg-cream-50" data-testid="impact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-widest text-teal-700">نتيجة رحلة التدريب التعاوني</div>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-heading font-black text-slate-900 leading-tight">
              أثر يُقاس ضمن منظومة اجتماعية
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed text-lg">
              بدل الاكتفاء بشهادات الإنجاز، تظهر لكل مستفيد لوحة توضح إنجازاته وأثر مهامه على المنظمة.
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-4 max-w-2xl" data-testid="impact-stats">
              <ImpactStat display={studentsDisplay} label="طالب مسجّل" testid="impact-stat-students" />
              <ImpactStat display={certsDisplay} label="شهادة موثّقة" testid="impact-stat-certs" />
              <ImpactStat display={assocsDisplay} label="جمعية شريكة" testid="impact-stat-assocs" />
              <ImpactStat display={opportunitiesDisplay} label="الفرص التدريبية" testid="impact-stat-opportunities" />
              <ImpactStat display={placementsDisplay} label="تسكين الطلاب" testid="impact-stat-placements" />
              <ImpactStat display={tasksDisplay} label="إجمالي المهام" testid="impact-stat-tasks" />
              <ImpactStat display={completedHoursDisplay} label="الساعات المنجزة" testid="impact-stat-completed-hours" />
              <ImpactStat display={sessionsDisplay} label="إجمالي الجلسات" testid="impact-stat-sessions" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-cream-200 bg-white p-4 lg:p-8 card-shadow-lg relative" data-testid="impact-featured-card">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs font-bold text-teal-700 uppercase tracking-widest inline-flex items-center gap-1.5">
                    {top && <Sparkles className="h-3.5 w-3.5 text-amber-500" />}
                    {top ? "الطالب المتفوّق هذا الشهر" : "تقرير الأداء الشهري"}
                  </div>
                  <h3 className="mt-1 font-heading font-black text-slate-900 text-xl" data-testid="impact-featured-name">
                    {card.name} — {card.program_label}
                  </h3>
                </div>
                <span className="text-xs text-slate-500 font-mono" dir="ltr">
                  {new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                </span>
              </div>
              <div className="mt-6 grid sm:grid-cols-4 gap-3">
                {[
                  { k: "الساعات المُقدَّمة", v: card.hours, tone: "teal" },
                  { k: "مهام مكتملة", v: `${card.tasks_done}/${card.tasks_total}`, tone: "amber" },
                  { k: "متوسط تقييم الأداء", v: `${card.eval_pct}%`, tone: "emerald" },
                  { k: "متوسط الحضور", v: `${card.attendance_pct}%`, tone: "sky" },
                ].map((s, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-cream-50 border border-cream-200">
                    <div className={`text-2xl lg:text-2xl font-heading font-black arabic-numerals ${
                      s.tone === "teal" ? "text-teal-700"
                      : s.tone === "emerald" ? "text-emerald-600"
                      : s.tone === "sky" ? "text-sky-600"
                      : "text-amber-600"
                    }`}>{s.v}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.k}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
                  <span>تقدّم الأداء</span>
                  <span className="font-bold text-slate-900 arabic-numerals">{card.progress_pct}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-cream-100 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-l from-amber-400 via-rose-400 to-teal-600 rounded-full transition-all"
                    style={{ width: `${Math.max(0, Math.min(100, card.progress_pct))}%` }}
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3 text-sm text-slate-600">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-teal-600 to-teal-900 text-white grid place-items-center font-bold overflow-hidden" aria-hidden="true">
                  {(card.name || "م").charAt(0)}
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-slate-500">الجمعية المستضيفة</div>
                  <div className="font-semibold text-slate-900">{card.association_label}</div>
                  <div className="text-xs text-slate-500">تدريب تعاوني مستمر منذ {card.since_months} أشهر</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
